import React, { ReactNode } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { CompanyModuleEnum, LovCompanyLocationWithOpeningHours, OpeningEntityTypeEnum } from '@api/ringil-api.v2';
import { ValidationError } from 'yup';

import Card from '@components/framework/card/Card.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';

import InputInheriterCompany from '@components/obsolete/Form/InputEntityOwner/InputInheriterCompany.tsx';
import InputEntityOwner from '@components/obsolete/Form/InputEntityOwner/InputEntityOwner.tsx';
import InputCompanyLocation from '@components/obsolete/Form/InputEntityOwner/InputCompanyLocation.tsx';
import ManageTimeslot from '@components/obsolete/TimeslotWithDefaults/ManageTimeslot.tsx';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect.tsx';
import InputCountry from '@components/framework/input/InputCountry/InputCountry.tsx';
import InputTimezone from '@components/ringil3/Input/InputTimezone/InputTimezone.tsx';
import { TimezoneAutoResolve } from '@hooks/useResolveTimezone/TimezoneAutoResolve.tsx';
import { SecuredConfig, useSecuredDecoration } from '@components/security/securedDecoration';

export interface ManualAddress {
  name?: string;
  city?: string;
  postalCode?: string;
  streetNr?: string;
  country?: string;
  timezoneId?: string;
}

export type LocationEntryMode = 'existing' | 'manual';

interface LocationCardProps {
  heading: string;
  icon?: JSX.Element;
  partySelector?: {
    role: 'consignor' | 'consignee';
    companyId: string;
    inheritorId?: string;
    onChangeCompany: (id: string) => void;
    onChangeCompanyCode?: (code: string) => void;
    // Generic config-driven flags (supplierAndCustomer): parent injects company-policy visibility/editability + required.
    secured?: SecuredConfig;
    required?: boolean;
  };
  location: {
    companyId: string;
    companyLocationId: string;
    onChange: (loc: LovCompanyLocationWithOpeningHours) => void;
    // Generic config-driven flag: parent injects the same company-policy (supplierAndCustomer) that drives the party — location follows its party.
    secured?: SecuredConfig;
    dataTestId?: string;
    // manual-entry seam — only rendered when allowManualEntry is true
    mode?: LocationEntryMode;
    onChangeMode?: (mode: LocationEntryMode) => void;
    manualAddress?: ManualAddress;
    onChangeManual?: (address: ManualAddress) => void;
    timezoneId?: string;
    onChangeTimezone?: (timezoneId: string) => void;
    manualErrorMessages?: Partial<Record<keyof ManualAddress | 'timezoneId', string>>;
  };
  // Divergent slot — rendered between location and date. SN passes nothing.
  // Expedition injection point: main-contact picker + additional-contacts list.
  contactSlot?: ReactNode;
  // Divergent slot — rendered at the bottom of the card. SN composes per-card extras here
  // (loading card: "+ Loading reference"; unloading card: notify-recipient control).
  extrasSlot?: ReactNode;
  timeslot?: {
    entityId: string;
    value: any;
    onChange: (v: any) => void;
    validationErrors?: ValidationError[];
    validationPrefix?: string;
    required?: boolean;
    // Generic config-driven flag: parent injects the company-policy visibility (shown in the security overlay).
    secured?: SecuredConfig;
    dataTestId?: string;
  };
  partyLabel?: string;
  partyErrorMessage?: string;
  locationLabel?: string;
  locationErrorMessage?: string;
  timeslotLabel?: string;
  partyDataTestId?: string;
  disabled?: boolean;
  allowManualEntry?: boolean;
}

const LocationCard: React.FC<LocationCardProps> = ({
  heading,
  icon,
  partySelector,
  location,
  contactSlot,
  extrasSlot,
  timeslot,
  partyLabel,
  partyErrorMessage,
  locationLabel,
  locationErrorMessage,
  timeslotLabel,
  partyDataTestId,
  disabled = false,
  allowManualEntry = false,
}) => {
  const { tr } = useTranslationLgs();

  const partyDecoration = useSecuredDecoration(partySelector?.secured);
  const locationDecoration = useSecuredDecoration(location.secured);
  const timeslotDecoration = useSecuredDecoration(timeslot?.secured);

  const mode: LocationEntryMode = location.mode ?? 'existing';

  const partyValue = partySelector?.role === 'consignor' ? partySelector?.inheritorId : partySelector?.companyId;
  const setManualField = (field: keyof ManualAddress, value: string) => location.onChangeManual?.({ ...(location.manualAddress ?? {}), [field]: value });
  const manualErr = location.manualErrorMessages ?? {};
  const manualDataTestId = location.dataTestId ? `${location.dataTestId}-manual` : undefined;

  return (
    <Card>
      <div className={'flex flex-col gap-4'}>
        <COHeadline title={heading} variant={'h4'} icon={icon} />

        {partySelector &&
          partyDecoration.decorate(
            <Field label={partyLabel} required={(partySelector.required ?? true) && !partyValue} errorMessage={partyErrorMessage}>
              {partySelector.role === 'consignor' ? (
                <InputInheriterCompany
                  companyId={partySelector.companyId}
                  inheriterId={partySelector.inheritorId}
                  onChange={partySelector.onChangeCompany}
                  module={CompanyModuleEnum.SUPPLIER_NOTIFICATION}
                  readonly={disabled || partyDecoration.editable === false}
                  filterEnabled={true}
                  autoSelectSingle={true}
                  dataTestId={partyDataTestId}
                />
              ) : (
                <InputEntityOwner
                  companyId={partySelector.companyId}
                  onChange={partySelector.onChangeCompany}
                  moduleType={CompanyModuleEnum.SUPPLIER_NOTIFICATION}
                  style={{ width: '100%' }}
                  onChangeCompanyCode={partySelector.onChangeCompanyCode}
                  readonly={disabled || partyDecoration.editable === false}
                  filterEnabled={true}
                  dataTestId={partyDataTestId}
                />
              )}
            </Field>,
          )}

        {allowManualEntry && (
          <InputSelect
            selectedItemCode={mode}
            model={[
              { label: tr('LocationCard.modeExisting', 'Existing'), code: 'existing' },
              { label: tr('LocationCard.modeManual', 'Enter manually'), code: 'manual' },
            ]}
            onSelect={v => location.onChangeMode?.(v as LocationEntryMode)}
            disabled={disabled}
            dataTestIdPrefix={location.dataTestId ? `${location.dataTestId}-mode` : undefined}
          />
        )}

        {mode === 'existing' &&
          locationDecoration.decorate(
            <Field label={locationLabel} required={!location.companyLocationId} errorMessage={locationErrorMessage}>
              <InputCompanyLocation
                moduleName={CompanyModuleEnum.SUPPLIER_NOTIFICATION}
                companyId={location.companyId}
                companyLocationId={location.companyLocationId}
                onChange={location.onChange}
                style={{ width: '100%' }}
                disabled={disabled || locationDecoration.editable === false}
                filterPartnerLocation={true}
                filterEnabled={true}
                autoSelectSingle={true}
                dataTestId={location.dataTestId}
              />
            </Field>,
          )}

        {allowManualEntry && mode === 'manual' && (
          <>
            <Field label={tr('generic.locationName', 'Location name')} required errorMessage={manualErr.name}>
              <RingilInputText
                value={location.manualAddress?.name}
                onChange={v => setManualField('name', v)}
                disabled={disabled}
                fullWidth
                dataTestId={manualDataTestId ? `${manualDataTestId}-locationName` : undefined}
              />
            </Field>
            <div className={'grid grid-cols-2 gap-4'}>
              <Field label={tr('generic.postalCode', 'Postal code')} required errorMessage={manualErr.postalCode}>
                <RingilInputText
                  value={location.manualAddress?.postalCode}
                  onChange={v => setManualField('postalCode', v)}
                  disabled={disabled}
                  fullWidth
                  dataTestId={manualDataTestId ? `${manualDataTestId}-postalCode` : undefined}
                />
              </Field>
              <Field label={tr('generic.country', 'Country')} required errorMessage={manualErr.country}>
                <InputCountry
                  value={location.manualAddress?.country}
                  onChange={v => setManualField('country', v)}
                  disabled={disabled}
                  dataTestId={manualDataTestId ? `${manualDataTestId}-country` : undefined}
                />
              </Field>
            </div>
            <Field label={tr('generic.city', 'City')} required errorMessage={manualErr.city}>
              <RingilInputText
                value={location.manualAddress?.city}
                onChange={v => setManualField('city', v)}
                disabled={disabled}
                fullWidth
                dataTestId={manualDataTestId ? `${manualDataTestId}-city` : undefined}
              />
            </Field>
            <Field label={tr('generic.streetNr', 'Street, number')} required errorMessage={manualErr.streetNr}>
              <RingilInputText
                value={location.manualAddress?.streetNr}
                onChange={v => setManualField('streetNr', v)}
                disabled={disabled}
                fullWidth
                dataTestId={manualDataTestId ? `${manualDataTestId}-street` : undefined}
              />
            </Field>
            <TimezoneAutoResolve
              countryCode3={location.manualAddress?.country}
              city={location.manualAddress?.city}
              postalCode={location.manualAddress?.postalCode}
              onResolved={tz => location.onChangeTimezone?.(tz)}
            />
            <Field label={tr('generic.timezoneId', 'Time zone')} required errorMessage={manualErr.timezoneId}>
              <InputTimezone
                value={location.timezoneId}
                onChange={v => location.onChangeTimezone?.(v)}
                disabled={disabled}
                dataTestId={manualDataTestId ? `${manualDataTestId}-timezone` : undefined}
              />
            </Field>
          </>
        )}

        {/* Expedition injection point: contact picker + additional-contacts list (SN passes nothing). */}
        {contactSlot}

        {timeslot &&
          timeslotDecoration.decorate(
            <Field label={timeslotLabel} required={timeslot.required}>
              <ManageTimeslot
                externalLabel
                entityType={OpeningEntityTypeEnum.LOCATION}
                entityId={timeslot.entityId}
                onChange={timeslot.onChange}
                timeslot={timeslot.value}
                value={timeslot.value?.dayWithInterval}
                validationErrors={timeslot.validationErrors}
                validationPrefix={timeslot.validationPrefix}
                dataTestId={timeslot.dataTestId}
              />
            </Field>,
          )}

        {/* Parent-composed extras at card bottom. SN: loading reference / notify-recipient.
            Expedition note: orders & unloading-routing stay parent-owned (right panel), never inside the card. */}
        {extrasSlot}
      </div>
    </Card>
  );
};

export default LocationCard;
